import {Link, TextField, Typography, WithStyles, withStyles} from '@material-ui/core';
import Button from "@components/common/button/Button";
import * as UserActions from '@services/user/user.actions';
import * as React from 'react';
import {connect} from 'react-redux';
import {Redirect, RouteComponentProps} from 'react-router-dom';
import TranslateNew from "@translate/TranslateNew";
import CordovaContext from 'src/cordovaContext';
import CornerRightElements2 from '@components/svg/cornerRightElements2';
import { CheckMark } from '@components/guide/components/CheckMarkSvg';
import { getOS } from '@helpers/osDetection';
import {CampaignContainerStyle as styles} from './campaign.style';
import { styleColors } from 'src/main.style';
import { attributedStoreUrl, prepareStoreUrl } from '@services/attribution/attribution';


const tn:TranslateNew = TranslateNew.getInstance();
const APP_STORE_URL = 'https://apps.apple.com/us/app/varannan-vecka/id1618305857';
const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=app.varannanvecka';

function safeDecodeUrlPart(value: string): string {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

function normalizeCampaignToken(token?: string): string {
  if (!token) {
    return "";
  }

  const decoded = safeDecodeUrlPart(token.trim());
  return decoded.split(/[?#&]/)[0].trim();
}


interface IState {
  token:string;
  hasSentData:boolean;
  useWeb:boolean;
  forceOnward:boolean; // a specified time has passed and we should just continue on
  offerAvailable:boolean|undefined; // the supplied or campaign code has an offer
  email:string;
  submittedEmail:boolean;
}

interface IProps extends RouteComponentProps<IRouteParams>, WithStyles<typeof styles>  {
  storeCampaignToken: (payload: string | { token: string; onComplete?: () => void }) => void;
  getCampaignToken: (email:string) => void;
  user: any;
  offers:Array<{value:string, name:string}>;
}

interface IRouteParams {
  token?: string;
}

class PCampaignContainer extends React.Component<IProps, IState> {

  private timer = setTimeout(() => this.setState({forceOnward:true}), 800);
  private submitRedirectMinTimer: any;
  private submitRedirectMaxTimer: any;
  private storeRedirectTimer: any;
  private submitRedirectFinished = false;
  private storeRedirectStarted = false;
  private isUnmounted = false;

  constructor(props:IProps) {
    super(props);

    props.getCampaignToken("x");

    const { match: { params: { token } } } = props;
    const campaignToken = normalizeCampaignToken(token);

    let hasSentData = false;
    if (campaignToken.length > 0) {
      props.storeCampaignToken(campaignToken);
      hasSentData = true;
    }

    const offerAvailable = this.hasOffer(campaignToken, this.props.offers);

    this.state = {
      token:campaignToken,
      hasSentData,
      useWeb:false,
      forceOnward:false,
      offerAvailable,
      email:"",
      submittedEmail:false,
    };
  }

  public componentDidUpdate(oldProps:IProps) {
    if (oldProps.match !== this.props.match) {
      const { match: { params: { token } } } = this.props;
      const campaignToken = normalizeCampaignToken(token);
      if(campaignToken.length > 0 && (!this.state.hasSentData || campaignToken !== this.state.token)) {
        this.props.storeCampaignToken(campaignToken);
        this.setState({hasSentData:true, token:campaignToken});
      }
      if (this.props.offers.length > 0 && campaignToken.length > 0) {
        const hasOffer = this.hasOffer(campaignToken, this.props.offers);
        if (hasOffer !== this.state.offerAvailable) {
          this.setState({offerAvailable:hasOffer});
        }
      }
    } else {
      const hasOffer = this.hasOffer(this.state.token, this.props.offers);
      if (hasOffer !== this.state.offerAvailable) {
        this.setState({offerAvailable:hasOffer});
      }
    }

  }

  public componentWillUnmount() {
    this.isUnmounted = true;
    clearTimeout(this.timer);
    clearTimeout(this.submitRedirectMinTimer);
    clearTimeout(this.submitRedirectMaxTimer);
    clearTimeout(this.storeRedirectTimer);
  }

  public render() {
    return (
      <CordovaContext.Consumer>
      {
      (cordovaApp) =>
        { return (cordovaApp
          ?this.renderApp()
          :this.renderWeb());
        }
      }
      </CordovaContext.Consumer>
    );
  }

  private renderApp() {
    return (<Redirect to='/login' />);
  }

  private renderWeb() {
    const os = getOS();
    const style = this.props.classes;

    if (this.state.forceOnward || this.state.offerAvailable === false) {
      if (os === "ANDROID") {
        return this.renderStoreRedirect(PLAY_STORE_URL);
      } else if (os === "IOS") {
        return this.renderStoreRedirect(APP_STORE_URL);
      } else if (this.state.useWeb) {
        return (<Redirect to='/signup' />);
      }
      return this.renderStoreFallback(false);
    } else if (this.state.offerAvailable) {
      const myOffer = this.state.token 
        ?this.props.offers.find((offer) => offer.value.toUpperCase() === this.state.token.toUpperCase())
        :undefined
      
      const benefactor = myOffer?myOffer.name:"unknown";

      return (
      <div className={style.root}>
        <div className={style.background}>
          <div className={style.circle}/>
          <CornerRightElements2 viewBox="-40 10 219 212" className={style.backgroundImgCornerTopRight}/>
        </div>
        <div className={style.container}>
          {this.state.submittedEmail && <div className={style.checkmarkPane}><CheckMark /></div>}
          <div className={style.centerContent}>
            <div className={style.topContent}>
              <Typography variant="h2">{tn.lang.campaignTitle}</Typography>
              <Typography>
                {tn.lang.campaignSpecialOffer(benefactor)}
              </Typography>
              <Typography>
                {tn.lang.campaignEnterEmail}
              </Typography>
            </div>
            <div className={style.formMiddleContent}>
              <TextField
                type="text"
                name="email"
                id="email"
                label={tn.lang.labelEmail}
                autoComplete="email"
                value={this.state.email}
                onChange={(e) => this.setState({email:e.target.value})}
                style={{backgroundColor:styleColors.white, marginBottom:'20px'}}
                variant='outlined'
                // onBlur={this.handleInputBlur}
                // error={formErrors.email}
              />
            </div>
            <div className={style.formBottomContent}>
              <Button
                  type="submit"
                  variant='contained'
                  color='primary'
                  size="large"
                  fullWidth={true}
                  disabled={this.state.submittedEmail}
                  onClick={() => this.submitCampaignEmail()}
              >
                  {tn.lang.submit}
              </Button>
              <Button
                  type="submit"
                  size="large"
                  fullWidth={true}
                  onClick={() => this.setState({forceOnward:true})}
              >
                  {tn.lang.noThanks}
              </Button>
            </div>
          </div>
        </div>
      </div>);
    } else {
      return (
        <div>
          checking...
        </div>);
    }

  }

  private renderStoreRedirect(storeUrl: string) {
    this.openStore(storeUrl);
    return this.renderStoreFallback(true);
  }

  private renderStoreFallback(openingStore: boolean) {
    const style = this.props.classes;

    return (
      <div className={style.contentBox}>
        <Typography variant="h6">
          {tn.lang.download} Varannan Vecka
        </Typography>
        {openingStore && (
          <Typography>
            Opening store...
          </Typography>
        )}
        <Link href={APP_STORE_URL}>
          <img src={require('src/images/badge-apple.png')} />
        </Link>
        <Link href={attributedStoreUrl(PLAY_STORE_URL)}>
          <img src={require('src/images/badge-google.png')} />
        </Link>
        <Button variant="contained" color="primary"  onClick={() => this.setState({useWeb:true})}>
          Use browser version
        </Button>
      </div>
    );
  }

  private openStore(storeUrl: string) {
    if (this.storeRedirectStarted) {
      return;
    }

    this.storeRedirectStarted = true;
    this.storeRedirectTimer = setTimeout(async () => {
      const destination = await prepareStoreUrl(storeUrl);
      if (!this.isUnmounted) {
        window.location.assign(destination);
      }
    }, 50);
  }


  private hasOffer(token:string|undefined, offers:Array<{value:string, name:string}>) {
    if (token === undefined) {return undefined;}
    const upperToken = token.toUpperCase();
    // if (token === "web") {return false;}
    if (offers.length === 0) {return undefined;}
    if (offers.length > 0 && token && token.length > 0) {
      const myOffer = offers.find((offer) => offer.value?.toUpperCase() === upperToken);

      if (myOffer !== undefined) {
        clearTimeout(this.timer);
        return true;
      } else {
        clearTimeout(this.timer);
        return false;
      }
    }
    return undefined;
  }

  private submitCampaignEmail() {
    if (this.state.submittedEmail) {
      return;
    }

    this.submitRedirectFinished = false;
    let minDelayDone = false;
    let backendDone = false;

    const finishWhenReady = () => {
      if (this.isUnmounted || this.submitRedirectFinished || !minDelayDone || !backendDone) {
        return;
      }

      this.submitRedirectFinished = true;
      clearTimeout(this.submitRedirectMinTimer);
      clearTimeout(this.submitRedirectMaxTimer);
      this.setState({forceOnward:true});
    };

    this.setState({submittedEmail:true, hasSentData: true});

    this.submitRedirectMinTimer = setTimeout(() => {
      minDelayDone = true;
      finishWhenReady();
    }, 800);

    this.submitRedirectMaxTimer = setTimeout(() => {
      backendDone = true;
      finishWhenReady();
    }, 5000);

    this.props.storeCampaignToken({
      token: this.state.token + ":" + this.state.email,
      onComplete: () => {
        backendDone = true;
        finishWhenReady();
      },
    });
  }



}

function mapStateToProps(state: any) {

  let offers:Array<{value:string, name:string}> = [];

  
  if (state.user.session && state.user.session.campaigns && state.user.session.campaigns.campaigns) {
    offers = state.user.session.campaigns.campaigns;
  }

  return {
    user: state.user.session?state.user.session.data:{},
    language: state.user.language,
    errorMessage: state.user.errorMessage.message,
    errorTime: state.user.errorMessage.id,
    offers
  };
}

const mapDispatchToProps = {
  storeCampaignToken: UserActions.storeCampaignToken,
  getCampaignToken: UserActions.getCampaignToken
};

const CampaignContainer = withStyles(styles)(connect(mapStateToProps, mapDispatchToProps)(PCampaignContainer));

export default CampaignContainer;



// WEBPACK FOOTER //
// ./src/containers/campaign/campaign.container.tsx